import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MunicipalityService } from '@app/shared/services/api/municipality.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { map as _map } from 'lodash';
import { MunicipalityOnboardingConfirmCancelModalComponent } from '../municipality-onboarding-confirm-cancel-modal/municipality-onboarding-confirm-cancel-modal.component';

@Component({
  selector: 'app-municipality-onboarding-modal',
  templateUrl: './municipality-onboarding-modal.component.html',
  styleUrls: ['./municipality-onboarding-modal.component.scss'],
})
export class MunicipalityOnboardingModalComponent implements OnInit {
  municipalityOnboardingForm: FormGroup = this.formBuilder.group({});
  isVerifying = false;
  currIndex = 0;
  isCancelShown = false;
  isDisabled = true;

  constructor(
    private readonly dialogRef: MatDialogRef<MunicipalityOnboardingModalComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly municipalityService: MunicipalityService,
    private readonly dialog: MatDialog,
    private overlayLoadingService: OverlayLoaderService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.municipalityOnboardingForm = this.formBuilder.group({
      keyArray: this.formBuilder.array([this.formBuilder.control('', [Validators.pattern('^(0[0-9]|1[0-6])[0-9]{6}'), Validators.minLength(8)])]),
    });
  }

  get keyArray(): FormArray {
    // tslint:disable-next-line
    return this.municipalityOnboardingForm.get('keyArray') as FormArray;
  }

  /**
   * closes the dialog
   */
  closeModal(): void {
    this.dialogRef.close();
  }

  /**
   *
   * @param i formControl index
   * @returns error string
   */
  displayErrorMessage(i: number) {
    const errors: ValidationErrors | null = this.keyArray.controls[i].errors;
    if (errors?.minlength || errors?.required) {
      this.isDisabled = true;
      return this.translate.instant('module.modals.onboarding.onboardingModalMinlengthErrorMessage');
    }
    if (errors?.pattern) {
      this.isDisabled = true;
      return this.translate.instant('module.modals.onboarding.onboardingModalPatternErrorMessage');
    }
    return '';
  }

  /**
   *
   * @param i formControl index
   */
  getFormControlStatus(i: number): void {
    this.isVerifying = true;
    this.isDisabled = true;
    this.currIndex = i;

    setTimeout(() => {
      const control = this.keyArray.controls[i];
      if (control.errors === null) {
        control.disable();
        this.isVerifying = false;
        this.isDisabled = !this.keyArray.controls.some((fcontrol) => fcontrol.disabled);
        control.markAsTouched({ onlySelf: true });
      } else {
        if (control.errors.required || control.errors.minlength || control.errors.pattern) {
          this.isVerifying = false;
          control.markAsPending({ onlySelf: true });
        }
      }
    }, 1000);
  }

  /**
   *
   * adds new formControl into array
   *
   * @returns
   */
  onAddFieldClick(): void {
    if (this.keyArray.controls.length <= 4) {
      this.keyArray.push(this.formBuilder.control('', [Validators.pattern('^(0[0-9]|1[0-6])[0-9]{6}'), Validators.minLength(8)]));
      this.isDisabled = true;
    }
  }

  /**
   * show or hide confirm cancel dialog
   *
   * @returns
   */
  onCancelClick(): void {
    const hasEntry = this.keyArray.controls.some((input) => input.value !== '');
    if (hasEntry === true && this.isCancelShown === false) {
      this.isCancelShown = true;
      const confirmDialogRef = this.dialog.open(MunicipalityOnboardingConfirmCancelModalComponent, {
        panelClass: ['-md', 'onboarding-confirm-cancel-dialog'],
        disableClose: true,
      });

      confirmDialogRef.afterClosed().subscribe((response) => {
        response ? this.dialogRef.close() : (this.isCancelShown = false);
      });

      return;
    }
    this.dialogRef.close();
  }

  /**
   *
   * @param i formControl index
   */
  onEditClick(i: number): void {
    this.isDisabled = true;
    this.keyArray.controls[i].enable();
  }

  /**
   * submits form
   */
  onSubmitClick(): void {
    this.overlayLoadingService.isLoading = true;
    const keyValues = this.keyArray
      .getRawValue()
      .filter((val) => val !== '')
      .join(',');

    this.municipalityService
      .addMunicipality(keyValues)
      .pipe(
        catchError((e) => {
          this.overlayLoadingService.isLoading = false;
          this.dialogRef.close({
            added: [],
            invalid: [],
            skipped: _map(this.keyArray.value, (keyValue) => {
              return { municipalityKey: keyValue, municipalityName: '' };
            }),
          });
          return of();
        }),
      )
      .subscribe((result) => {
        this.overlayLoadingService.isLoading = false;
        this.dialogRef.close(result);
      });
  }

  /**
   *
   * @param i formControl index
   */
  removeField(i: number): void {
    this.currIndex = 0;
    this.keyArray.removeAt(i);
    this.isDisabled = !this.keyArray.controls.every((control) => control.disabled);
  }
}
